/**
 * Modul 1111 - chart sloj na Apache ECharts (canvas).
 *
 * Drzi ISTE potpise kao raniji rucni SVG sloj (Series, labels, onPick, picked),
 * pa pozivna mjesta u ado1111Module.tsx ostaju netaknuta osim novog opcionalnog
 * `format` propa za tooltipe. Sve interakcije: crosshair + bogati tooltip,
 * klik bilo gdje u stupcu/tocki -> onPick(index), dataZoom (kotacic + slider
 * na dugim serijama), glatke tranzicije pri promjeni filtera, saveAsImage.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import * as echarts from 'echarts/core';
import { BarChart as EBarChart, HeatmapChart as EHeatmapChart, LineChart as ELineChart, ScatterChart as EScatterChart } from 'echarts/charts';
import {
    BrushComponent, DataZoomInsideComponent, DataZoomSliderComponent, GeoComponent, GridComponent,
    MarkAreaComponent, MarkLineComponent, ToolboxComponent, TooltipComponent, VisualMapComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([
    ELineChart, EBarChart, EHeatmapChart, EScatterChart,
    GridComponent, TooltipComponent, DataZoomInsideComponent, DataZoomSliderComponent,
    MarkLineComponent, MarkAreaComponent, ToolboxComponent, VisualMapComponent,
    GeoComponent, BrushComponent,
    CanvasRenderer,
]);

export type Series = { name: string; color: string; dash?: boolean; values: (number | null)[] };

// Paleta identicna C iz ado1111Module.tsx (namjerno duplicirana da nema kruznog importa).
const CC = {
    surface: '#ffffff',
    border: '#e5e7eb',
    borderSoft: '#f0f1f3',
    text: '#111827',
    muted: '#6b7280',
    soft: '#9ca3af',
};

const nf0 = new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 });
const nf2 = new Intl.NumberFormat('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function axisNumber(v: number): string {
    const a = Math.abs(v);
    if (a >= 1_000_000) return nf2.format(v / 1_000_000) + 'M';
    if (a >= 10_000) return nf0.format(v / 1000) + 'k';
    return nf0.format(v);
}

function hexToRgba(hex: string, alpha: number): string {
    const h = hex.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function vGradient(color: string, topA: number, bottomA: number): object {
    return {
        type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [
            { offset: 0, color: hexToRgba(color, topA) },
            { offset: 1, color: hexToRgba(color, bottomA) },
        ],
    };
}

const TOOLTIP_BOX = {
    backgroundColor: CC.surface,
    borderColor: CC.border,
    borderWidth: 1,
    padding: [10, 12] as [number, number],
    textStyle: { color: CC.text, fontSize: 12, fontFamily: 'inherit' },
    extraCssText: 'box-shadow: 0 10px 30px rgba(17, 24, 39, 0.14); border-radius: 10px;',
};

type TooltipParam = { seriesName?: string; color?: string; value?: number | null; axisValueLabel?: string; dataIndex?: number };

function tooltipHtml(head: string, rows: { color: string; name: string; text: string }[]): string {
    let html = `<div style="font-weight:700;margin-bottom:6px;font-size:12px">${head}</div>`;
    for (const r of rows) {
        html += `<div style="display:flex;align-items:center;gap:7px;line-height:1.9">`
            + `<span style="width:8px;height:8px;border-radius:50%;background:${r.color};flex:none"></span>`
            + `<span style="color:${CC.muted}">${r.name}</span>`
            + `<b style="margin-left:auto;padding-left:18px;font-variant-numeric:tabular-nums">${r.text}</b></div>`;
    }
    return html;
}

function seriesTooltipFormatter(fmt: (v: number) => string) {
    return (params: unknown): string => {
        const arr = (Array.isArray(params) ? params : [params]) as TooltipParam[];
        const rows = arr
            .filter((p) => p.value !== null && p.value !== undefined && typeof p.value === 'number')
            .map((p) => ({ color: String(p.color ?? CC.text), name: p.seriesName ?? '', text: fmt(p.value as number) }));
        return tooltipHtml(arr[0]?.axisValueLabel ?? '', rows);
    };
}

const SAVE_TOOLBOX = {
    show: true, right: 4, top: 0, itemSize: 13,
    feature: {
        saveAsImage: {
            title: 'PNG', pixelRatio: 2, backgroundColor: CC.surface,
            iconStyle: { borderColor: CC.soft },
            emphasis: { iconStyle: { borderColor: CC.text } },
        },
    },
};

// ---------------------------------------------------------------- EChart host

function EChart({ option, height, onIndexClick }: {
    option: echarts.EChartsCoreOption; height: number; onIndexClick?: (i: number) => void;
}) {
    const divRef = useRef<HTMLDivElement | null>(null);
    const chartRef = useRef<echarts.ECharts | null>(null);
    const clickRef = useRef<typeof onIndexClick>(onIndexClick);
    clickRef.current = onIndexClick;

    useEffect(() => {
        const el = divRef.current;
        if (!el) return;
        const chart = echarts.init(el);
        chartRef.current = chart;
        const ro = new ResizeObserver(() => chart.resize());
        ro.observe(el);
        chart.getZr().on('click', (e: { offsetX: number; offsetY: number }) => {
            const cb = clickRef.current;
            if (!cb) return;
            const pt: [number, number] = [e.offsetX, e.offsetY];
            if (!chart.containPixel('grid', pt)) return;
            const res = chart.convertFromPixel({ seriesIndex: 0 }, pt) as unknown;
            const i = Array.isArray(res) ? Math.round(Number(res[0])) : NaN;
            if (Number.isFinite(i) && i >= 0) cb(i);
        });
        return () => { ro.disconnect(); chart.dispose(); chartRef.current = null; };
    }, []);

    useEffect(() => {
        // merge + replaceMerge('series') = glatka tranzicija starih serija, bez ostataka uklonjenih
        chartRef.current?.setOption(option, { replaceMerge: ['series'] });
    }, [option]);

    return <div ref={divRef} style={{ width: '100%', height, cursor: onIndexClick ? 'pointer' : 'default' }} />;
}

// ---------------------------------------------------------------- LineChart

/** Linijski graf za vremenske dimenzije. Klik na tocku/stupac -> onPick(index). */
export function LineChart({ series, labels, height = 280, onPick, picked, format }: {
    series: Series[]; labels: string[]; height?: number; onPick?: (i: number) => void; picked?: number | null; format?: (v: number) => string;
}) {
    const fmt = format ?? ((v: number) => nf2.format(v));
    const option = useMemo<echarts.EChartsCoreOption>(() => {
        const n = labels.length;
        const zoom = n > 45;
        const solids = series.filter((s) => !s.dash).length;
        return {
            animationDuration: 550,
            animationDurationUpdate: 420,
            animationEasing: 'cubicOut',
            grid: { left: 62, right: 18, top: 26, bottom: zoom ? 56 : 30 },
            toolbox: SAVE_TOOLBOX,
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross',
                    label: { backgroundColor: CC.text, fontSize: 10, formatter: (p: { axisDimension?: string; value?: unknown }) => (p.axisDimension === 'y' ? axisNumber(Number(p.value)) : String(p.value ?? '')) },
                    crossStyle: { color: CC.soft },
                    lineStyle: { color: CC.soft, type: [4, 4] },
                },
                formatter: seriesTooltipFormatter(fmt),
                ...TOOLTIP_BOX,
            },
            xAxis: {
                type: 'category', data: labels, boundaryGap: false,
                axisLine: { lineStyle: { color: CC.border } },
                axisTick: { show: false },
                axisLabel: { color: CC.muted, fontSize: 10, hideOverlap: true, margin: 12 },
            },
            yAxis: {
                type: 'value',
                axisLabel: { color: CC.soft, fontSize: 10, formatter: (v: number) => axisNumber(v) },
                splitLine: { lineStyle: { color: CC.borderSoft } },
            },
            dataZoom: zoom
                ? [
                    { type: 'inside', throttle: 40 },
                    {
                        type: 'slider', height: 20, bottom: 6, borderColor: 'transparent',
                        backgroundColor: CC.borderSoft, fillerColor: hexToRgba('#2563eb', 0.10),
                        handleStyle: { color: CC.surface, borderColor: CC.soft },
                        moveHandleStyle: { color: CC.soft }, emphasis: { moveHandleStyle: { color: CC.muted } },
                        dataBackground: { lineStyle: { color: CC.soft }, areaStyle: { color: CC.border } },
                        selectedDataBackground: { lineStyle: { color: CC.muted }, areaStyle: { color: CC.border } },
                        textStyle: { color: CC.soft, fontSize: 9 },
                    },
                ]
                : [{ type: 'inside', throttle: 40 }],
            series: series.map((s, si) => ({
                id: `${si}:${s.name}`,
                name: s.name,
                type: 'line',
                data: s.values,
                smooth: 0.22,
                showSymbol: n <= 40,
                symbol: 'circle',
                symbolSize: 5.5,
                connectNulls: false,
                z: s.dash ? 2 : 3,
                lineStyle: { width: s.dash ? 1.8 : 2.6, type: s.dash ? [6, 5] : 'solid', color: s.color, cap: 'round', join: 'round' },
                itemStyle: { color: s.color, borderColor: CC.surface, borderWidth: 1.5 },
                emphasis: { focus: series.length > 2 ? 'series' : 'none', scale: 1.6 },
                // ispuna samo za pune serije; kod vise punih serija stisaj da se ne blati
                areaStyle: s.dash ? undefined : { color: vGradient(s.color, solids > 1 ? 0.14 : 0.22, 0.01) },
                universalTransition: true,
                markLine: si === 0 && picked !== null && picked !== undefined && picked >= 0 && picked < n
                    ? {
                        silent: true, symbol: 'none', animation: false,
                        data: [{ xAxis: picked }],
                        lineStyle: { color: CC.text, width: 1.2, type: 'solid', opacity: 0.55 },
                        label: { show: false },
                    }
                    : undefined,
            })),
        };
    }, [series, labels, picked, fmt]);

    return <EChart option={option} height={height} onIndexClick={onPick} />;
}

// ---------------------------------------------------------------- GroupedBars

/** Grupirani stupci za kategorijske dimenzije. Klik na grupu -> onPick(index). */
export function GroupedBars({ series, labels, height = 300, onPick, picked, horizontalLabels = false, format }: {
    series: Series[]; labels: string[]; height?: number; onPick?: (i: number) => void; picked?: number | null; horizontalLabels?: boolean; format?: (v: number) => string;
}) {
    const fmt = format ?? ((v: number) => nf2.format(v));
    const option = useMemo<echarts.EChartsCoreOption>(() => {
        const n = labels.length;
        const zoom = n > 24;
        return {
            animationDuration: 550,
            animationDurationUpdate: 420,
            animationEasing: 'cubicOut',
            grid: { left: 62, right: 18, top: 26, bottom: (horizontalLabels ? 28 : 72) + (zoom ? 28 : 0) },
            toolbox: SAVE_TOOLBOX,
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow', shadowStyle: { color: hexToRgba('#111827', 0.05) } },
                formatter: seriesTooltipFormatter(fmt),
                ...TOOLTIP_BOX,
            },
            xAxis: {
                type: 'category', data: labels,
                axisLine: { lineStyle: { color: CC.border } },
                axisTick: { show: false },
                axisLabel: {
                    color: CC.muted, fontSize: 10, interval: 0, hideOverlap: true,
                    rotate: horizontalLabels ? 0 : 38,
                    formatter: (v: string) => (v.length > 18 ? v.slice(0, 17) + '…' : v),
                },
            },
            yAxis: {
                type: 'value',
                axisLabel: { color: CC.soft, fontSize: 10, formatter: (v: number) => axisNumber(v) },
                splitLine: { lineStyle: { color: CC.borderSoft } },
            },
            dataZoom: zoom
                ? [
                    { type: 'inside', throttle: 40 },
                    {
                        type: 'slider', height: 20, bottom: 6, borderColor: 'transparent',
                        backgroundColor: CC.borderSoft, fillerColor: hexToRgba('#2563eb', 0.10),
                        handleStyle: { color: CC.surface, borderColor: CC.soft },
                        textStyle: { color: CC.soft, fontSize: 9 },
                    },
                ]
                : undefined,
            series: series.map((s, si) => ({
                id: `${si}:${s.name}`,
                name: s.name,
                type: 'bar',
                data: s.values,
                barMaxWidth: 26,
                barGap: '25%',
                itemStyle: {
                    color: s.dash ? hexToRgba(s.color, 0.45) : vGradient(s.color, 1, 0.72),
                    borderRadius: [5, 5, 0, 0],
                },
                emphasis: { itemStyle: { shadowBlur: 12, shadowColor: hexToRgba(s.color, 0.4) } },
                universalTransition: true,
                markArea: si === 0 && picked !== null && picked !== undefined && picked >= 0 && picked < n
                    ? {
                        silent: true, animation: false,
                        itemStyle: { color: hexToRgba('#111827', 0.055) },
                        data: [[{ xAxis: labels[picked] }, { xAxis: labels[picked] }]],
                    }
                    : undefined,
            })),
        };
    }, [series, labels, picked, horizontalLabels, fmt]);

    return <EChart option={option} height={height} onIndexClick={onPick} />;
}

// ---------------------------------------------------------------- Heatmap

export type HeatCellLike = { dow: number; hour: number; type: string; broj: number; eur: number };
const DOWS_HR = ['Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub', 'Ned'];
const DOWS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Heatmap dan-u-tjednu x sat. */
export function HeatmapGrid({ cells, metric, types, locale, formatEur }: {
    cells: HeatCellLike[]; metric: 'eur' | 'broj'; types: string[]; locale: 'hr' | 'en'; formatEur: (v: number) => string;
}) {
    const option = useMemo<echarts.EChartsCoreOption>(() => {
        const dows = locale === 'hr' ? DOWS_HR : DOWS_EN;
        const agg = new Map<string, number>();
        for (const c of cells) {
            if (!types.includes(c.type)) continue;
            const k = c.dow + '|' + c.hour;
            agg.set(k, (agg.get(k) ?? 0) + (metric === 'eur' ? c.eur : c.broj));
        }
        const data: [number, number, number][] = [];
        for (let d = 0; d < 7; d++) for (let h = 0; h < 24; h++) data.push([h, d, agg.get(d + '|' + h) ?? 0]);
        const maxV = Math.max(1, ...agg.values());
        return {
            animationDuration: 450,
            grid: { left: 44, right: 12, top: 8, bottom: 40 },
            tooltip: {
                position: 'top',
                formatter: (p: unknown) => {
                    const v = (p as { value: [number, number, number] }).value;
                    const txt = metric === 'eur' ? formatEur(v[2]) : nf0.format(v[2]) + ' txn';
                    return tooltipHtml(`${dows[v[1]]} ${v[0]}:00 - ${v[0]}:59`, [{ color: '#2563eb', name: metric === 'eur' ? 'EUR' : (locale === 'hr' ? 'Broj' : 'Count'), text: txt }]);
                },
                ...TOOLTIP_BOX,
            },
            xAxis: {
                type: 'category', data: Array.from({ length: 24 }, (_, h) => String(h)),
                axisLine: { show: false }, axisTick: { show: false },
                axisLabel: { color: CC.soft, fontSize: 9, interval: 1 },
                splitArea: { show: false },
            },
            yAxis: {
                type: 'category', data: dows, inverse: true,
                axisLine: { show: false }, axisTick: { show: false },
                axisLabel: { color: CC.muted, fontSize: 10 },
            },
            visualMap: {
                min: 0, max: maxV, calculable: false, orient: 'horizontal', left: 44, bottom: 2,
                itemHeight: 90, itemWidth: 10, precision: 0,
                text: [axisNumber(maxV), '0'], textStyle: { color: CC.soft, fontSize: 9 },
                inRange: { color: [CC.borderSoft, '#bfdbfe', '#3b82f6', '#1e40af'] },
            },
            series: [{
                type: 'heatmap',
                data,
                itemStyle: { borderColor: CC.surface, borderWidth: 2, borderRadius: 4 },
                emphasis: { itemStyle: { shadowBlur: 8, shadowColor: hexToRgba('#1e40af', 0.45) } },
                label: { show: false },
            }],
        };
    }, [cells, metric, types, locale, formatEur]);

    return <EChart option={option} height={7 * 26 + 60} />;
}

// ---------------------------------------------------------------- Spark

let sparkSeq = 0;

/** Mini sparkline (mjesecni trend poslovnice): gladak SVG s gradijent ispunom, bez echarts instance po retku. */
export function Spark({ points, color = CC.muted, width = 120, height = 28 }: { points: number[]; color?: string; width?: number; height?: number }) {
    const idRef = useRef<string>('');
    if (idRef.current === '') idRef.current = 'm1111spark' + ++sparkSeq;
    if (points.length === 0) return <span style={{ color: CC.soft }}>-</span>;
    const maxV = Math.max(...points, 1);
    const minV = Math.min(...points, 0);
    const span = maxV - minV || 1;
    const x = (i: number) => (points.length <= 1 ? width / 2 : (i * (width - 4)) / (points.length - 1) + 2);
    const y = (v: number) => height - 3 - ((v - minV) / span) * (height - 6);
    // glatka krivulja kroz srednje tocke (quadratic midpoint)
    let d = `M ${x(0)} ${y(points[0])}`;
    for (let i = 1; i < points.length; i++) {
        const mx = (x(i - 1) + x(i)) / 2;
        const my = (y(points[i - 1]) + y(points[i])) / 2;
        d += ` Q ${x(i - 1)} ${y(points[i - 1])} ${mx} ${my}`;
    }
    d += ` L ${x(points.length - 1)} ${y(points[points.length - 1])}`;
    const area = d + ` L ${x(points.length - 1)} ${height - 1} L ${x(0)} ${height - 1} Z`;
    const gid = idRef.current;
    return (
        <svg width={width} height={height} style={{ display: 'block' }}>
            <defs>
                <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={color} stopOpacity={0.02} />
                </linearGradient>
            </defs>
            <path d={area} fill={`url(#${gid})`} stroke="none" />
            <path d={d} fill="none" stroke={color} strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />
            <circle cx={x(points.length - 1)} cy={y(points[points.length - 1])} r={2.2} fill={color} />
        </svg>
    );
}

// ---------------------------------------------------------------- GeoScatterMap

/** Tocka na karti: poslovnica s vrijednoscu metrike po tipu transakcije. */
export type GeoPoint = { branch: string; lat: number; lng: number; perType: Record<string, number | null> };

let geoRegistered = false;
async function ensureGeoMap(): Promise<void> {
    if (geoRegistered) return;
    // Lazy: GeoJSON (Natural Earth 50m, 15 zemalja JI Europe) ne ulazi u glavni chunk.
    const mod = await import('./ado1111GeoSE.json');
    echarts.registerMap('m1111-se-europe', mod.default as unknown as Parameters<typeof echarts.registerMap>[1]);
    geoRegistered = true;
}

type BrushSelectedBatch = { batch?: { selected?: { seriesIndex?: number; dataIndex?: number[] }[] }[] };

/**
 * Geo karta poslovnica: scatter po tipu transakcije (boja tipa, velicina =
 * metrika), roam zoom/pan, brush select (pravokutnik/lasso iz toolboxa) vraca
 * nazive zahvacenih poslovnica kroz onSelectionChange. Klik na tocku ->
 * onPointClick(branch).
 */
export function GeoScatterMap({ points, types, height = 560, format, onSelectionChange, onPointClick }: {
    points: GeoPoint[];
    types: { key: string; name: string; color: string }[];
    height?: number;
    format: (v: number) => string;
    onSelectionChange?: (branches: string[]) => void;
    onPointClick?: (branch: string) => void;
}) {
    const divRef = useRef<HTMLDivElement | null>(null);
    const chartRef = useRef<echarts.ECharts | null>(null);
    const [ready, setReady] = useState(geoRegistered);
    // Nazivi po seriji (za mapiranje brush dataIndex -> poslovnica) i callbacki u refovima.
    const namesRef = useRef<string[][]>([]);
    const selRef = useRef<string[]>([]);
    const selCbRef = useRef<typeof onSelectionChange>(onSelectionChange);
    const clickCbRef = useRef<typeof onPointClick>(onPointClick);
    selCbRef.current = onSelectionChange;
    clickCbRef.current = onPointClick;

    useEffect(() => {
        let alive = true;
        void ensureGeoMap().then(() => { if (alive) setReady(true); });
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (!ready) return;
        const el = divRef.current;
        if (!el) return;
        const chart = echarts.init(el);
        chartRef.current = chart;
        const ro = new ResizeObserver(() => chart.resize());
        ro.observe(el);
        chart.on('brushSelected', (raw: unknown) => {
            const p = raw as BrushSelectedBatch;
            const out = new Set<string>();
            for (const b of p.batch ?? []) {
                for (const s of b.selected ?? []) {
                    const names = namesRef.current[s.seriesIndex ?? -1] ?? [];
                    for (const i of s.dataIndex ?? []) {
                        if (names[i] !== undefined) out.add(names[i]);
                    }
                }
            }
            const arr = [...out].sort();
            if (arr.join('|') !== selRef.current.join('|')) {
                selRef.current = arr;
                selCbRef.current?.(arr);
            }
        });
        chart.on('click', (raw: unknown) => {
            const p = raw as { componentType?: string; name?: string };
            if (p.componentType === 'series' && p.name) clickCbRef.current?.(p.name);
        });
        return () => { ro.disconnect(); chart.dispose(); chartRef.current = null; };
    }, [ready]);

    const option = useMemo<echarts.EChartsCoreOption>(() => {
        const byBranch = new Map<string, GeoPoint>();
        for (const p of points) byBranch.set(p.branch, p);
        let maxV = 0;
        for (const p of points) for (const ty of types) maxV = Math.max(maxV, Math.abs(p.perType[ty.key] ?? 0));
        if (maxV <= 0) maxV = 1;
        const seriesNames: string[][] = [];
        const series = types.map((ty) => {
            const data = points
                .filter((p) => (p.perType[ty.key] ?? 0) !== 0)
                .map((p) => ({ name: p.branch, value: [p.lng, p.lat, Math.abs(p.perType[ty.key] ?? 0)] as [number, number, number] }));
            seriesNames.push(data.map((d) => d.name));
            return {
                name: ty.name,
                type: 'scatter' as const,
                coordinateSystem: 'geo' as const,
                geoIndex: 0,
                data,
                symbolSize: (v: number[]) => 6 + 30 * Math.sqrt(v[2] / maxV),
                itemStyle: { color: hexToRgba(ty.color, 0.68), borderColor: CC.surface, borderWidth: 1 },
                emphasis: { scale: 1.25, itemStyle: { color: ty.color, shadowBlur: 14, shadowColor: hexToRgba(ty.color, 0.5) } },
                z: 3,
            };
        });
        namesRef.current = seriesNames;
        return {
            animationDuration: 500,
            animationDurationUpdate: 380,
            geo: {
                map: 'm1111-se-europe',
                roam: true,
                center: [16.9, 44.6],
                zoom: 5.4,
                scaleLimit: { min: 2.2, max: 40 },
                itemStyle: { areaColor: '#eef0f3', borderColor: '#c9d0d8', borderWidth: 0.8 },
                emphasis: { disabled: true },
                select: { disabled: true },
                tooltip: { show: false },
            },
            toolbox: {
                show: true, right: 8, top: 8, itemSize: 15, itemGap: 10,
                iconStyle: { borderColor: CC.soft },
                emphasis: { iconStyle: { borderColor: CC.text } },
                feature: {
                    brush: { title: { rect: 'Okvir', polygon: 'Laso', clear: 'Očisti odabir' } },
                    saveAsImage: { title: 'PNG', pixelRatio: 2, backgroundColor: CC.surface },
                },
            },
            brush: {
                toolbox: ['rect', 'polygon', 'clear'],
                geoIndex: 0,
                throttleType: 'debounce',
                throttleDelay: 250,
                brushStyle: { borderWidth: 1.2, color: 'rgba(37, 99, 235, 0.08)', borderColor: 'rgba(37, 99, 235, 0.6)' },
                inBrush: { opacity: 1 },
                outOfBrush: { colorAlpha: 0.16 },
            },
            tooltip: {
                trigger: 'item',
                formatter: (raw: unknown) => {
                    const p = raw as { name?: string };
                    const gp = p.name !== undefined ? byBranch.get(p.name) : undefined;
                    if (!gp) return '';
                    const rows = types
                        .map((ty) => ({ color: ty.color, name: ty.name, v: gp.perType[ty.key] }))
                        .filter((r): r is { color: string; name: string; v: number } => r.v !== null && r.v !== undefined && r.v !== 0)
                        .map((r) => ({ color: r.color, name: r.name, text: format(r.v) }));
                    return tooltipHtml(gp.branch, rows);
                },
                ...TOOLTIP_BOX,
            },
            series,
        };
    }, [points, types, format]);

    useEffect(() => {
        if (!ready) return;
        chartRef.current?.setOption(option, { replaceMerge: ['series'] });
    }, [option, ready]);

    if (!ready) {
        return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: CC.soft, fontSize: 13 }}>Karta se učitava…</div>;
    }
    return <div ref={divRef} style={{ width: '100%', height }} />;
}

// ---------------------------------------------------------------- AnimatedNumber

/** Broj koji se glatko dotjera na novu vrijednost (KPI kartice). */
export function AnimatedNumber({ value, format, duration = 600 }: { value: number | null; format: (v: number) => string; duration?: number }) {
    const [shown, setShown] = useState<number | null>(value);
    const fromRef = useRef<number | null>(value);
    const rafRef = useRef<number>(0);

    useEffect(() => {
        const from = fromRef.current;
        if (value === null || from === null || from === value) {
            fromRef.current = value;
            setShown(value);
            return;
        }
        const t0 = performance.now();
        const tick = (t: number) => {
            const p = Math.min(1, (t - t0) / duration);
            const e = 1 - Math.pow(1 - p, 3); // easeOutCubic
            setShown(from + (value - from) * e);
            if (p < 1) rafRef.current = requestAnimationFrame(tick);
            else fromRef.current = value;
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [value, duration]);

    return <>{shown === null ? '-' : format(shown)}</>;
}
